export type FolderStorageProgressState = {
  active: boolean;
  progress: number;
  label: string;
  error: string | null;
  done: number;
  total: number;
};

type FolderJobState = FolderStorageProgressState & {
  folder: string;
};

const IDLE: FolderStorageProgressState = {
  active: false,
  progress: 0,
  label: "",
  error: null,
  done: 0,
  total: 0,
};

const jobsByKey = new Map<string, FolderJobState>();
const abortByKey = new Map<string, () => void>();
const listeners = new Set<() => void>();
const aggregateCache = new Map<number, FolderStorageProgressState>();

function jobKey(accountId: number, folder: string): string {
  return `${accountId}\0${folder}`;
}

function progressEquals(
  a: FolderStorageProgressState,
  b: FolderStorageProgressState
): boolean {
  return (
    a.active === b.active &&
    a.progress === b.progress &&
    a.label === b.label &&
    a.error === b.error &&
    a.done === b.done &&
    a.total === b.total
  );
}

function emit(): void {
  aggregateCache.clear();
  for (const cb of listeners) cb();
}

function aggregateAccount(accountId: number): FolderStorageProgressState {
  const prefix = `${accountId}\0`;
  const jobs: FolderJobState[] = [];
  for (const [key, job] of jobsByKey) {
    if (key.startsWith(prefix)) jobs.push(job);
  }

  const activeJobs = jobs.filter((job) => job.active);
  let next: FolderStorageProgressState;
  if (activeJobs.length > 0) {
    const primary = activeJobs[activeJobs.length - 1];
    const extra = activeJobs.length - 1;
    next = {
      active: true,
      progress: primary.progress,
      label:
        extra > 0
          ? `${primary.label} (+${extra} autre${extra > 1 ? "s" : ""})`
          : primary.label,
      error: activeJobs.find((job) => job.error)?.error ?? null,
      done: primary.done,
      total: primary.total,
    };
  } else {
    const errored = jobs.find((job) => job.error);
    if (errored) {
      next = {
        active: false,
        progress: errored.progress,
        label: errored.label,
        error: errored.error,
        done: errored.done,
        total: errored.total,
      };
    } else {
      next = IDLE;
    }
  }

  const cached = aggregateCache.get(accountId);
  if (cached && progressEquals(cached, next)) {
    return cached;
  }
  aggregateCache.set(accountId, next);
  return next;
}

export function getFolderStorageProgress(
  accountId?: number
): FolderStorageProgressState {
  if (accountId == null) return IDLE;
  return aggregateAccount(accountId);
}

/** Premier job de copie .eml actif (tous comptes) — pour le journal. */
export function getPrimaryActiveFolderStorageProgress(): FolderStorageProgressState | null {
  let last: FolderJobState | null = null;
  for (const job of jobsByKey.values()) {
    if (job.active) last = job;
  }
  if (!last) {
    for (const job of jobsByKey.values()) {
      if (job.error) {
        return {
          active: false,
          progress: job.progress,
          label: job.label,
          error: job.error,
          done: job.done,
          total: job.total,
        };
      }
    }
    return null;
  }
  return {
    active: true,
    progress: last.progress,
    label: last.label,
    error: last.error,
    done: last.done,
    total: last.total,
  };
}

export function isFolderStorageSyncActive(accountId: number, folder: string): boolean {
  return jobsByKey.get(jobKey(accountId, folder))?.active === true;
}

export function subscribeFolderStorageProgress(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

export function setFolderStorageProgress(
  accountId: number,
  folder: string,
  patch: Partial<FolderStorageProgressState> & { abort?: () => void }
): void {
  const key = jobKey(accountId, folder);
  if (patch.abort) {
    abortByKey.set(key, patch.abort);
  }

  const { abort: _abort, ...rest } = patch;
  const prev = jobsByKey.get(key) ?? { ...IDLE, folder };
  const next: FolderJobState = { ...prev, ...rest, folder };

  if (next.active === false && !next.error) {
    jobsByKey.delete(key);
    abortByKey.delete(key);
  } else {
    jobsByKey.set(key, next);
  }

  emit();
}

export function cancelFolderStorageDownload(accountId: number, folder?: string): void {
  if (folder != null) {
    abortByKey.get(jobKey(accountId, folder))?.();
    abortByKey.delete(jobKey(accountId, folder));
    jobsByKey.delete(jobKey(accountId, folder));
    emit();
    return;
  }

  for (const key of [...abortByKey.keys()]) {
    if (!key.startsWith(`${accountId}\0`)) continue;
    abortByKey.get(key)?.();
    abortByKey.delete(key);
    jobsByKey.delete(key);
  }
  emit();
}
